/**
 * useDeals — paginated, cached read of deals via the active CRM adapter.
 *
 * - First call returns { items: [], loading: true } then resolves.
 * - Subsequent calls with the same filterId hit the in-memory cache for 30s.
 * - `loadMore()` appends the next page using the cursor returned by the adapter.
 * - `refresh()` clears the cache key and re-fetches page 1.
 *
 * Cache lives at module scope so navigating away and back keeps results warm.
 * Not persistent across reloads — that's fine, Phase 2 doesn't need it.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCrm } from '../contexts/CrmContext';
import { AdapterError } from '../lib/crm/adapter';
import type { Deal, ListDealsResult } from '../lib/crm/types';

interface CacheEntry {
  pages: ListDealsResult[];
  ts: number;
}

const CACHE_TTL_MS = 30_000;
const cache = new Map<string, CacheEntry>();

interface UseDealsOpts {
  filterId?: string;
  pageSize?: number;
  /** Skip the initial fetch — call `refresh()` manually later. */
  manual?: boolean;
}

interface UseDealsReturn {
  items: Deal[];
  loading: boolean;
  loadingMore: boolean;
  error: AdapterError | null;
  hasMore: boolean;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
}

function cacheKey(crmType: string, filterId: string | undefined, pageSize: number): string {
  return `${crmType}:${filterId ?? '__all__'}:${pageSize}`;
}

export function useDeals(opts: UseDealsOpts = {}): UseDealsReturn {
  const { adapter } = useCrm();
  const { filterId, pageSize = 25, manual = false } = opts;

  const key = adapter ? cacheKey(adapter.type, filterId, pageSize) : null;

  const [pages, setPages] = useState<ListDealsResult[]>(() => {
    if (!key) return [];
    const hit = cache.get(key);
    if (hit && Date.now() - hit.ts < CACHE_TTL_MS) return hit.pages;
    return [];
  });
  const [loading, setLoading] = useState<boolean>(!manual && pages.length === 0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<AdapterError | null>(null);

  // Ref so concurrent calls don't double-fetch.
  const inflight = useRef(false);

  const fetchPage = useCallback(
    async (cursor: string | null): Promise<ListDealsResult | null> => {
      if (!adapter) return null;
      try {
        const result = await adapter.listDeals({
          filterId,
          pageSize,
          cursor: cursor ?? undefined,
        });
        return result;
      } catch (err) {
        const e =
          err instanceof AdapterError
            ? err
            : new AdapterError('UNKNOWN', err instanceof Error ? err.message : String(err));
        setError(e);
        return null;
      }
    },
    [adapter, filterId, pageSize],
  );

  const refresh = useCallback(async () => {
    if (!adapter || !key || inflight.current) return;
    inflight.current = true;
    setLoading(true);
    setError(null);
    const first = await fetchPage(null);
    if (first) {
      const next = [first];
      setPages(next);
      cache.set(key, { pages: next, ts: Date.now() });
    }
    setLoading(false);
    inflight.current = false;
  }, [adapter, key, fetchPage]);

  const loadMore = useCallback(async () => {
    if (!adapter || !key || inflight.current) return;
    const last = pages[pages.length - 1];
    if (!last?.nextCursor) return;
    inflight.current = true;
    setLoadingMore(true);
    const next = await fetchPage(last.nextCursor);
    if (next) {
      const allPages = [...pages, next];
      setPages(allPages);
      cache.set(key, { pages: allPages, ts: Date.now() });
    }
    setLoadingMore(false);
    inflight.current = false;
  }, [adapter, key, pages, fetchPage]);

  // Auto-fetch first page on mount (unless manual or cache-warm).
  useEffect(() => {
    if (manual || !adapter || !key) return;
    if (pages.length > 0) {
      setLoading(false);
      return;
    }
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapter, key]);

  const items = useMemo(() => pages.flatMap((p) => p.items), [pages]);
  const hasMore = pages.length > 0 && pages[pages.length - 1]?.nextCursor !== null;

  return {
    items,
    loading,
    loadingMore,
    error,
    hasMore,
    refresh,
    loadMore,
  };
}

/** Test/storybook helper — clear the entire deals cache. */
export function clearDealsCache() {
  cache.clear();
}
